import { ProfileMeResponse, ProfileRoleDetails, ProfileTenant, ResolvedPermission, UserModel } from './_models'
import { isSuperAdminUser, normalizeResolvedPermissionMap } from './permissionUtils'

export type NormalizedProfilePayload = {
  user: UserModel | undefined
  roleDetails: ProfileRoleDetails | null
  tenant: ProfileTenant | null
  permissions: Record<string, ResolvedPermission>
}

export const normalizeResolvedPermissions = (
  permissionsMap?: Record<string, ResolvedPermission> | unknown[]
): Record<string, ResolvedPermission> => {
  return normalizeResolvedPermissionMap(permissionsMap)
}

const resolveRawProfilePermissions = (payload: any): Record<string, ResolvedPermission> | unknown[] | undefined => {
  const topLevelPermissions = payload?.permissions

  if (Array.isArray(payload?.effectivePermissions)) {
    return payload.effectivePermissions as unknown[]
  }

  if (Array.isArray(topLevelPermissions)) {
    return topLevelPermissions as unknown[]
  }

  if (topLevelPermissions && typeof topLevelPermissions === 'object') {
    if (Array.isArray(topLevelPermissions.effectivePermissions)) {
      return topLevelPermissions.effectivePermissions as unknown[]
    }

    if (topLevelPermissions.permissions && typeof topLevelPermissions.permissions === 'object') {
      return topLevelPermissions.permissions as Record<string, ResolvedPermission>
    }

    return topLevelPermissions as Record<string, ResolvedPermission>
  }

  return undefined
}

export const normalizeProfilePayload = (
  payload: ProfileMeResponse | UserModel
): NormalizedProfilePayload => {
  const asAny = payload as any
  const hasWrapper = Boolean(
    asAny?.user ||
    asAny?.permissions ||
    asAny?.effectivePermissions ||
    asAny?.tenant ||
    asAny?.roleDetails
  )
  const wrappedUser = asAny?.user as UserModel | undefined
  const user: UserModel | undefined = hasWrapper
    ? ({
      ...(wrappedUser || {}),
      role: asAny?.role ?? wrappedUser?.role,
      rank: asAny?.rank ?? wrappedUser?.rank,
      vessel: asAny?.vessel ?? wrappedUser?.vessel,
      roleEntityId: asAny?.roleEntityId ?? wrappedUser?.roleEntityId,
      roleEntityName: asAny?.roleEntityName ?? wrappedUser?.roleEntityName,
      companyGroupAdminId: asAny?.companyGroupAdminId ?? wrappedUser?.companyGroupAdminId,
      companyAdminId: asAny?.companyAdminId ?? wrappedUser?.companyAdminId,
      companyGroupAdmin: asAny?.companyGroupAdmin ?? wrappedUser?.companyGroupAdmin,
      companyAdmin: asAny?.companyAdmin ?? wrappedUser?.companyAdmin,
    } as UserModel)
    : (payload as UserModel)
  const role = hasWrapper ? (asAny?.roleDetails as ProfileRoleDetails | undefined) : undefined
  const tenantContext = hasWrapper ? (asAny?.tenant as ProfileTenant | undefined) : undefined
  const permissionsMap = hasWrapper ? resolveRawProfilePermissions(asAny) : undefined

  return {
    user,
    roleDetails: role ?? null,
    tenant: tenantContext ?? null,
    permissions: normalizeResolvedPermissions(permissionsMap),
  }
}

export const resolveIsSuperAdmin = (
  currentUser?: UserModel,
  roleDetails?: ProfileRoleDetails | null
): boolean =>
  roleDetails?.effectiveBehaviorType === 'SUPER_ADMIN' ||
  roleDetails?.code === 'SUPER_ADMIN' ||
  currentUser?.role?.roleName === 'SUPER_ADMIN' ||
  isSuperAdminUser(currentUser)
